import { createApiClient, type ApiClient, type Tokens } from '@ddt/api-client';
import { createDesktopPlatform, createWebPlatform, desktopBridge, jsonStore, type KeyValueStorage, type Platform, type PlatformEnv } from '@ddt/platform';

export interface Services {
  platform: Platform;
  api: ApiClient;
  /** Chamado quando a sessão não pode mais ser renovada; o app volta para a tela de entrada. */
  sessionExpired: { handler: () => void };
}

export const SESSION_KEY = 'ddt.session.v1';

function isTokens(value: unknown): value is Tokens {
  const v = value as Partial<Tokens> | null;
  return typeof v === 'object' && v !== null && typeof v.accessToken === 'string' && typeof v.refreshToken === 'string' && typeof v.accessExpiresAt === 'number';
}

export function defaultEnv(): PlatformEnv {
  return {
    apiBase: import.meta.env.VITE_API_BASE ?? 'http://localhost:5000/api',
    wsUrl: import.meta.env.VITE_WS_URL ?? 'ws://localhost:5000/api/match/ws',
    assetsUrl: import.meta.env.VITE_ASSETS_URL ?? '/assets',
  };
}

export interface ServiceOptions {
  env?: PlatformEnv;
  storage?: KeyValueStorage;
  fetch?: typeof fetch;
}

export function createServices(options: ServiceOptions = {}): Services {
  // Dentro do Electron a preload entrega a plataforma (env, cofre e links); no navegador vale o ambiente do Vite.
  const bridge = options.env || options.storage ? null : desktopBridge();
  const env = options.env ?? bridge?.env ?? defaultEnv();
  const platform = bridge ? createDesktopPlatform(bridge) : createWebPlatform({ env, storage: options.storage });
  const tokens = jsonStore<Tokens>(platform.storage, SESSION_KEY, isTokens);
  const sessionExpired = { handler: () => undefined as void };
  const api = createApiClient({
    baseUrl: env.apiBase,
    tokens,
    fetch: options.fetch,
    onSessionExpired: () => sessionExpired.handler(),
  });
  return { platform, api, sessionExpired };
}

let current: Services | null = null;

export function getServices(): Services {
  current ??= createServices();
  return current;
}

/** Troca os serviços (testes e inicialização). */
export function setServices(services: Services | null): void {
  current = services;
}
